import React from 'react';
import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LoadedContent,
} from '@occc-app/design-system';
import { useIsUserPermittedTo, Feature, useAccessControlContext } from '@occc/utils-access-control';
import { KioskAdSetFragment, User_Role } from '@occc/graphql';

import { AdSetsListTableRow } from './ad-sets-list.table-row';

import { OfferLinkInfo } from '../../utils/offer-url';

const convertToApolloError = (error: Error): ApolloError => {
  return new ApolloError({
    errorMessage: error.message,
    graphQLErrors: [],
    networkError: null,
  });
};

type AdSetsListTableProps = {
  adSets?: KioskAdSetFragment[];
  offersMap: Map<string, OfferLinkInfo>;
  loading: boolean;
  error?: Error;
  paginationProps: IPaginationProps;
  onItemClick: (adSetId: string) => void;
  onView: (adSetId: string) => void;
  onEdit: (adSetId: string) => void;
  onDelete: (adSetId: string) => void;
  onPublish: (adSetId: string) => void;
  onArchive: (adSetId: string) => void;
};

export const AdSetsListTable = React.memo<AdSetsListTableProps>(
  ({
    adSets,
    offersMap,
    loading,
    error,
    paginationProps,
    onItemClick,
    onView,
    onEdit,
    onDelete,
    onPublish,
    onArchive,
  }) => {
    const isUserPermittedToEditAdSet = useIsUserPermittedTo(Feature.TOUCHPOINT_AD_SET_EDIT);
    const isUserPermittedToViewAdSet = useIsUserPermittedTo(Feature.TOUCHPOINT_AD_SET_VIEW);
    const accessControlContext = useAccessControlContext();
    const isAdminUser = accessControlContext?.userRole === User_Role.Admin;

    const renderData = (items: KioskAdSetFragment[]) => {
      const rows = items.map((adSet) => (
        <AdSetsListTableRow
          key={adSet.id}
          adSet={adSet}
          offer={adSet.offerId ? offersMap.get(adSet.offerId) : undefined}
          actions={{
            onItemClick,
            onView,
            onEdit,
            onDelete,
            onPublish,
            onArchive,
          }}
          permissions={{
            canEdit: isUserPermittedToEditAdSet,
            canView: isUserPermittedToViewAdSet,
          }}
          isAdminUser={isAdminUser}
        />
      ));

      return (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 600, md: 'auto' }, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>Name</TableCell>
                  <TableCell sx={{ width: '25%' }}>Description</TableCell>
                  <TableCell sx={{ width: '10%' }}>Status</TableCell>
                  <TableCell sx={{ width: '15%' }}>Reward</TableCell>
                  <TableCell sx={{ width: '15%' }}>Asset Name</TableCell>
                  <TableCell align='right' sx={{ width: 80 }}>
                    Image
                  </TableCell>
                  {isAdminUser && <TableCell align='right' sx={{ width: 56 }}></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>{rows}</TableBody>
            </Table>
          </Box>
          <Pagination {...paginationProps} />
        </>
      );
    };

    return (
      <LoadedContent<KioskAdSetFragment>
        error={error ? convertToApolloError(error) : undefined}
        loading={loading}
        items={adSets}
        renderData={renderData}
      />
    );
  },
);
